import { pipe } from 'ramda';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SubmissionHoursConstrainType } from 'api/timesheets/configuration/submission-hours';
import { InputNumber } from 'components/atoms/input';
import { Hours } from 'components/organisms/user-capacity';
import { filter, map, ofNullable, orElse } from 'core/maybe';
import { unreachable } from 'utils/unreachable';
import { Box } from '../components';

const min = 1;
const max = 168;
const Input = styled(InputNumber).attrs({
  min,
  max,
})`
  width: 100px;
`;

interface Props {
  choosenConstrainType: SubmissionHoursConstrainType;
  constrainType: SubmissionHoursConstrainType;
  hours: Hours;
  onChange: (newHours: Hours) => void;
  capacity: Hours;
}
const ConstrainWithoutHours = [SubmissionHoursConstrainType.IsEmpty];
const getTranslationKey = (type: SubmissionHoursConstrainType): string => {
  switch (type) {
    case SubmissionHoursConstrainType.IsEmpty:
      return 'empty';
    case SubmissionHoursConstrainType.LessThan:
      return 'less_than';

    case SubmissionHoursConstrainType.GreaterThan:
      return 'greater_than';

    case SubmissionHoursConstrainType.Equals:
      return 'equals';
    default:
      return unreachable(type);
  }
};

export const ConstrainItem: FC<Props> = ({
  choosenConstrainType,
  constrainType,
  hours,
  onChange,
  capacity,
}) => {
  const hasHoursInput = !ConstrainWithoutHours.includes(constrainType);
  const canShowHours = hasHoursInput && choosenConstrainType === constrainType;
  const onHoursChange = useCallback(
    (next) => {
      onChange(
        pipe(
          () => next?.toString() ?? null,
          ofNullable,
          map((x) => parseInt(x, 10)),
          filter((x) => !Number.isNaN(x)),
          map((x) => Math.min(x, max)),
          map((x) => Math.max(x, min)),
          orElse(capacity),
        )(),
      );
    },
    [capacity, onChange],
  );

  return (
    <Box>
      <FormattedMessage
        id={`settings.timesheets.controls.submission_hours.constrain.${getTranslationKey(
          constrainType,
        )}`}
      />
      {canShowHours ? (
        <>
          <Input
            defaultValue={capacity ?? 1}
            onChange={onHoursChange}
            value={hours}
          />
          <FormattedMessage
            id="settings.timesheets.controls.capacity.units"
            values={{ count: hours }}
          />
        </>
      ) : null}
    </Box>
  );
};
